/**
 * BoardLayoutEditor — visual editor for a single board layout.
 *
 * 3-column layout (mirrors TemplateEditor):
 *   ┌─────────────┬──────────────────────┬───────────────┐
 *   │  Palette    │   Canvas             │  Properties   │
 *   │  (frame     │   (reference photo + │  panel        │
 *   │   types)    │    draggable frames) │               │
 *   └─────────────┴──────────────────────┴───────────────┘
 *
 * Frame coords are normalised 0–100% so the same layout overlays
 * cleanly on any phone-photo size during the camera capture flow
 * (phase 2).
 *
 * Frame types: 'breaker' | 'isolator' | 'circuit_label'.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, Trash2, Upload, X, Image as ImageIcon, Move, Hash, Copy, ClipboardPaste, Wand2, Ruler, Sparkles, Palette, ChevronDown,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  getBoardLayout, updateBoardLayout, uploadBoardLayoutPhoto,
  deleteBoardLayoutPhoto, fetchBoardLayoutPhotoBlobUrl,
  snapBoardLayoutFrames, equaliseBoardLayoutFrames,
  generateBoardLayoutFrames, detectBoardLayoutPhases,
  fetchBoardLayoutFrameCropBlobUrl,
} from '../../services/api';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import FrameCropPreview from '../../components/FrameCropPreview';

// Frame palette — these are the drag-source chips on the left.
const FRAME_TYPES = [
  {
    type: 'breaker',
    label: 'Breaker',
    desc: 'Single MCB / RCBO position',
    color: 'bg-blue-500/15 border-blue-500',
    chip: 'bg-blue-500',
    defaultSize: { w: 4, h: 8 },
  },
  {
    type: 'isolator',
    label: 'Isolator',
    desc: 'Main switch / isolator',
    color: 'bg-amber-500/15 border-amber-500',
    chip: 'bg-amber-500',
    defaultSize: { w: 12, h: 10 },
  },
  {
    type: 'circuit_label',
    label: 'Circuit label',
    desc: 'Description card (e.g. Sockets)',
    color: 'bg-emerald-500/15 border-emerald-500',
    chip: 'bg-emerald-500',
    defaultSize: { w: 16, h: 6 },
  },
];

const frameMeta = (type) => FRAME_TYPES.find((t) => t.type === type) || FRAME_TYPES[0];

// Snap to 0.5% so movements are smooth but not pixel-jittery.
const snap = (v) => Math.round(v * 2) / 2;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Generate a per-session id for new frames; overwritten by backend on save.
const tempId = () => `t${Math.random().toString(36).slice(2, 10)}`;

// Pull the most useful error message out of an axios failure for the AI
// helper toolbar (Generate / Snap / Equalise / Detect phases). FastAPI
// validation errors expose `detail` as an array; auth & app errors as a
// string; network failures have no response at all. Without this, the
// toast collapses to a generic "Generate failed" and the engineer can't
// tell the network from the photo from the dimensions.
const aiToolErrorMessage = (e, fallback) => {
  // No response = network/CORS/timeout. Surface the axios message so the
  // engineer sees something like "Network Error" or "timeout of 30000ms".
  if (!e?.response) {
    const msg = e?.message || 'Network error';
    return `${fallback}: ${msg} (no response from server)`;
  }
  const status = e.response.status;
  const detail = e.response.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return `${fallback} (HTTP ${status}): ${detail}`;
  }
  if (Array.isArray(detail) && detail.length) {
    // FastAPI 422 — first validation message is usually enough.
    const first = detail[0];
    const loc = Array.isArray(first?.loc) ? first.loc.join('.') : '';
    const msg = first?.msg || 'validation error';
    return `${fallback} (HTTP ${status}): ${loc} — ${msg}`;
  }
  return `${fallback} (HTTP ${status})`;
};

/**
 * Mirror of the backend's `_build_slots` so we can show the slot list
 * the moment the engineer changes Phases or Ways — without waiting
 * for a save+reload round-trip. Same shape as the API's `slots` field.
 */
const buildSlotsClient = (phases, ways) => {
  const p = phases === 1 || phases === 3 ? phases : 3;
  const w = Math.max(0, Math.min(60, Number(ways) || 0));
  if (w <= 0) return [];
  const slots = [];
  let cct = 0;
  for (let way = 1; way <= w; way += 1) {
    for (let L = 1; L <= p; L += 1) {
      cct += 1;
      slots.push({ cctId: cct, label: `${way}L${L}`, way, line: L });
    }
  }
  return slots;
};

/**
 * Canonical-string snapshot of the bits of `layout` that the save
 * payload sends to the backend. Used as the comparison key for the
 * "Unsaved changes" pill — anything that round-trips through the
 * server is in here, anything purely client-side (selectionIds,
 * clipboard, blob URLs, etc.) is not.
 */
const buildLayoutSnapshot = (l) => {
  if (!l) return '';
  return JSON.stringify({
    name: l.name || '',
    description: l.description || '',
    phases: Number(l.phases) || 3,
    ways: Number(l.ways) || 0,
    frames: (l.frames || []).map((f) => ({
      id: f.id && !String(f.id).startsWith('t') ? f.id : '',
      type: f.type,
      label: f.label || '',
      x: Number(f.x) || 0,
      y: Number(f.y) || 0,
      w: Number(f.w) || 0,
      h: Number(f.h) || 0,
      cctId: Number(f.cctId) || 0,
    })),
  });
};

const BoardLayoutEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Snapshot of the last-known-saved layout state. We compare the
  // current `layout` against this to surface an "Unsaved changes" pill
  // and to gate the AI tools (Generate / Snap / Equalise / Detect-
  // phases) so they always operate on a flushed-to-DB layout — the
  // backend reads phases/ways/frames straight from the doc, so a
  // dirty form silently producing stale-DB results was a real source
  // of confusion in production.
  const [savedSnapshot, setSavedSnapshot] = useState('');
  // Multi-selection: a Set of frame IDs. Keeps backwards-compat with
  // the old `selectedId` single-select via the `selectedId` derived
  // value below ("the most-recently-clicked frame" — that's what the
  // properties panel shows).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lastSelectedId, setLastSelectedId] = useState(null);
  // Frame-clipboard: an array of frames (without their ids) captured
  // by Cmd/Ctrl+C. Pasting (Cmd/Ctrl+V) re-creates them with fresh
  // IDs and a small offset so they don't sit on top of the originals.
  const [frameClipboard, setFrameClipboard] = useState([]);
  // Object URL for the reference photo (we have to download it via an
  // authenticated fetch since <img src> can't carry a Bearer token).
  const [photoBlobUrl, setPhotoBlobUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Server-side AI crop preview — when the engineer clicks "Verify
  // server crop" we fetch the actual bytes the AI training queue + cert
  // vision pipeline would receive for the selected frame. The local
  // canvas preview (`FrameCropPreview`) tries to mirror the math, but
  // this is the ground truth: if the two ever diverge, we want the
  // engineer to see it BEFORE running a 500-card training queue.
  const [serverCropUrl, setServerCropUrl] = useState(null);
  const [serverCropLoading, setServerCropLoading] = useState(false);
  const [serverCropError, setServerCropError] = useState(null);
  const [serverCropFrameId, setServerCropFrameId] = useState(null);
  // Convenience getters used everywhere below.
  const selectedId = lastSelectedId;
  const selectedCount = selectedIds.size;

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await getBoardLayout(id);
      const next = {
        ...data,
        // Ensure frames have IDs we can React-key on.
        frames: (data.frames || []).map((f) => ({ ...f, id: f.id || tempId() })),
      };
      setLayout(next);
      setSavedSnapshot(buildLayoutSnapshot(next));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load layout');
      navigate('/admin/board-layouts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  // Whenever the layout's hasPhoto flag flips, fetch the reference
  // photo as an authenticated blob and feed an object URL into <img>.
  // (We can't put the bearer token on a plain <img src>.)
  useEffect(() => {
    if (!layout?.hasPhoto) {
      setPhotoBlobUrl(null);
      return undefined;
    }
    let alive = true;
    let resolvedUrl = null;
    fetchBoardLayoutPhotoBlobUrl(id)
      .then((u) => {
        if (!alive) {
          URL.revokeObjectURL(u);
          return;
        }
        resolvedUrl = u;
        setPhotoBlobUrl(u);
      })
      .catch(() => {
        if (alive) setPhotoBlobUrl(null);
      });
    return () => {
      alive = false;
      if (resolvedUrl) URL.revokeObjectURL(resolvedUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout?.hasPhoto, id]);

  // Single-frame selection (replace) — used by Canvas click. Shift /
  // Ctrl-click branches into the toggle path so engineers can build
  // up a selection and group-move them together.
  const selectFrame = (frameId, opts = {}) => {
    if (!frameId) {
      setSelectedIds(new Set());
      setLastSelectedId(null);
      return;
    }
    if (opts.toggle) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(frameId)) next.delete(frameId);
        else next.add(frameId);
        return next;
      });
    } else {
      setSelectedIds(new Set([frameId]));
    }
    setLastSelectedId(frameId);
  };

  const isFrameSelected = (frameId) => selectedIds.has(frameId);

  // Move every selected frame by the same dx/dy% — the engine
  // backing FrameBox calls into this when the user drags any frame
  // that's in the multi-selection (otherwise the lone-frame fast
  // path runs). Each call re-clamps so dragging towards the edge
  // doesn't push frames off-canvas; we compute the smallest allowed
  // dx/dy across the whole selection so the GROUP moves together
  // (rather than individual frames stopping at the edge while their
  // neighbours keep going).
  const moveSelectedFrames = (dx, dy) => {
    setLayout((l) => {
      const idsArr = [...selectedIds];
      if (!idsArr.length) return l;
      let minX = 100, minY = 100, maxRightSlack = 100, maxBottomSlack = 100;
      l.frames.forEach((f) => {
        if (!selectedIds.has(f.id)) return;
        if (f.x < minX) minX = f.x;
        if (f.y < minY) minY = f.y;
        const rs = 100 - (f.x + f.w);
        const bs = 100 - (f.y + f.h);
        if (rs < maxRightSlack) maxRightSlack = rs;
        if (bs < maxBottomSlack) maxBottomSlack = bs;
      });
      const allowedDx = clamp(dx, -minX, maxRightSlack);
      const allowedDy = clamp(dy, -minY, maxBottomSlack);
      return {
        ...l,
        frames: l.frames.map((f) =>
          selectedIds.has(f.id)
            ? { ...f, x: snap(f.x + allowedDx), y: snap(f.y + allowedDy) }
            : f,
        ),
      };
    });
  };

  // Copy currently-selected frames into the in-memory clipboard.
  // Strips IDs so paste creates fresh ones.
  const copySelected = () => {
    if (!selectedIds.size) return toast.error('No frames selected');
    const snapshot = layout.frames
      .filter((f) => selectedIds.has(f.id))
      .map(({ id: _id, ...rest }) => ({ ...rest }));
    setFrameClipboard(snapshot);
    toast.success(`Copied ${snapshot.length} frame${snapshot.length === 1 ? '' : 's'}`);
  };

  // Paste clipboard frames at a 3% / 3% offset so they don't sit
  // exactly on top of the originals. Re-clamps if the offset would
  // push any frame off-canvas. New frames become the current
  // selection so the engineer can immediately drag them.
  const pasteFromClipboard = () => {
    if (!frameClipboard.length) return toast.error('Clipboard is empty — copy some frames first');
    const offsetX = 3, offsetY = 3;
    const newFrames = frameClipboard.map((f) => ({
      ...f,
      id: tempId(),
      x: clamp(snap(f.x + offsetX), 0, 100 - f.w),
      y: clamp(snap(f.y + offsetY), 0, 100 - f.h),
    }));
    setLayout((l) => ({ ...l, frames: [...l.frames, ...newFrames] }));
    setSelectedIds(new Set(newFrames.map((f) => f.id)));
    setLastSelectedId(newFrames[newFrames.length - 1].id);
    toast.success(`Pasted ${newFrames.length} frame${newFrames.length === 1 ? '' : 's'}`);
  };

  // Delete every selected frame.
  const deleteSelected = () => {
    if (!selectedIds.size) return;
    setLayout((l) => ({ ...l, frames: l.frames.filter((f) => !selectedIds.has(f.id)) }));
    setSelectedIds(new Set());
    setLastSelectedId(null);
  };

  // Keyboard shortcuts: Cmd/Ctrl+C, Cmd/Ctrl+V, Backspace/Delete,
  // Cmd/Ctrl+A (select-all). Bound at the document level but only
  // act when the focus isn't inside a text input.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      const cmd = e.metaKey || e.ctrlKey;
      if (cmd && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        copySelected();
      } else if (cmd && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        pasteFromClipboard();
      } else if (cmd && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        if (layout?.frames?.length) {
          setSelectedIds(new Set(layout.frames.map((f) => f.id)));
          setLastSelectedId(layout.frames[layout.frames.length - 1].id);
        }
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.size) {
        e.preventDefault();
        deleteSelected();
      } else if (e.key === 'Escape') {
        selectFrame(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout?.frames, selectedIds, frameClipboard]);

  // Upsert a frame in the local state (does NOT save until user clicks Save).
  const updateFrame = (frameId, patch) => {
    setLayout((l) => ({
      ...l,
      frames: l.frames.map((f) => (f.id === frameId ? { ...f, ...patch } : f)),
    }));
  };

  const addFrame = (type) => {
    const meta = frameMeta(type);
    const f = {
      id: tempId(),
      type,
      label: '',
      x: 40,
      y: 40,
      ...meta.defaultSize,
    };
    setLayout((l) => ({ ...l, frames: [...l.frames, f] }));
    selectFrame(f.id);
  };

  const removeFrame = (frameId) => {
    setLayout((l) => ({ ...l, frames: l.frames.filter((f) => f.id !== frameId) }));
    setSelectedIds((prev) => {
      if (!prev.has(frameId)) return prev;
      const next = new Set(prev);
      next.delete(frameId);
      return next;
    });
    if (lastSelectedId === frameId) setLastSelectedId(null);
  };

  /**
   * Auto-assign every slot (1L1, 1L2, …) to a breaker frame in
   * row-major order — sort frames top-to-bottom then left-to-right
   * (using each frame's center) and zip with the slot list.
   * Frames without an assigned slot keep their existing label/cctId.
   */
  const autoNumberFrames = () => {
    const slots = liveSlots;
    if (!slots.length) return toast.error('Set Phases and Ways first');
    const breakers = layout.frames.filter((f) => f.type === 'breaker');
    if (!breakers.length) return toast.error('Add some breaker frames first');
    // Sort by row band first (group rows by ~10% Y bands), then by X within row
    const ROW_BAND = 8; // % — frames within this Y delta count as same row
    const sorted = [...breakers].sort((a, b) => {
      const ay = a.y + a.h / 2, by = b.y + b.h / 2;
      const ax = a.x + a.w / 2, bx = b.x + b.w / 2;
      if (Math.abs(ay - by) > ROW_BAND) return ay - by;
      return ax - bx;
    });
    const assigned = new Map();
    sorted.slice(0, slots.length).forEach((f, i) => {
      const slot = slots[i];
      assigned.set(f.id, { label: slot.label, cctId: slot.cctId });
    });
    setLayout((l) => ({
      ...l,
      frames: l.frames.map((f) =>
        assigned.has(f.id)
          ? { ...f, ...assigned.get(f.id) }
          : f
      ),
    }));
    toast.success(`Numbered ${assigned.size} breaker frame${assigned.size === 1 ? '' : 's'}`);
  };

  /**
   * Assign a specific slot to a frame (manual override). Pass slot=null
   * to clear the frame's slot binding.
   */
  const assignSlotToFrame = (frameId, slot) => {
    setLayout((l) => ({
      ...l,
      frames: l.frames.map((f) =>
        f.id === frameId
          ? { ...f, label: slot?.label || '', cctId: slot?.cctId || 0 }
          : f
      ),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: layout.name,
        description: layout.description || '',
        phases: layout.phases || 3,
        ways: layout.ways || 0,
        frames: layout.frames.map((f) => ({
          type: f.type,
          label: f.label || '',
          x: f.x,
          y: f.y,
          w: f.w,
          h: f.h,
          cctId: Number(f.cctId) || 0,
          // Only forward stable IDs; new ones get backend-assigned UUIDs.
          ...(f.id && !f.id.startsWith('t') ? { id: f.id } : {}),
        })),
      };
      const updated = await updateBoardLayout(id, payload);
      const next = {
        ...updated,
        frames: (updated.frames || []).map((f) => ({ ...f, id: f.id || tempId() })),
      };
      setLayout(next);
      setSavedSnapshot(buildLayoutSnapshot(next));
      toast.success('Layout saved');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // True when the in-memory form differs from the last-saved server
  // state. Drives the "Unsaved changes" pill + auto-save guard.
  const isDirty = useMemo(
    () => layout != null && buildLayoutSnapshot(layout) !== savedSnapshot,
    [layout, savedSnapshot],
  );

  // Flush any pending edits before an AI tool fires. The Generate /
  // Snap / Equalise / Detect-phases endpoints all read phases / ways
  // / frames straight from the saved DB doc, so a dirty form would
  // otherwise produce results based on stale state. Returns true on
  // a clean (or successfully-flushed) layout, false if save failed.
  const ensureSavedBeforeAITool = async (toolLabel) => {
    if (!isDirty) return true;
    toast.info(`Saving ${toolLabel ? `before ${toolLabel}` : 'unsaved changes'}…`);
    return await save();
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    // HEIC/HEIF reach us as `image/heic`, `image/heif`, or sometimes
    // `application/octet-stream` from iOS Safari — in that last case
    // the filename suffix is the only signal. The backend converts
    // HEIC → JPEG on upload so storage stays in baseline web formats.
    const allowedMimes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const heicSuffix = /\.(heic|heif|heics|heifs)$/i.test(file.name || '');
    if (!allowedMimes.includes(file.type) && !heicSuffix) {
      return toast.error('Only JPG / PNG / WEBP / HEIC supported');
    }
    if (file.size > 10 * 1024 * 1024) {
      return toast.error('Photo must be under 10 MB');
    }
    setUploadingPhoto(true);
    try {
      const updated = await uploadBoardLayoutPhoto(id, file);
      setLayout((l) => ({ ...l, hasPhoto: updated.hasPhoto, photoMime: updated.photoMime }));
      toast.success('Reference photo uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePhotoRemove = async () => {
    if (!window.confirm('Remove the reference photo? Frames will be kept.')) return;
    try {
      await deleteBoardLayoutPhoto(id);
      setLayout((l) => ({ ...l, hasPhoto: false, photoMime: null }));
      toast.success('Photo removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  // Auto-snap to grid — backend reads the reference photo's pixel data
  // and pulls each frame's edges onto the nearest strong gradient line.
  // Called from the canvas toolbar; a save is implicit since the
  // backend already persists the new coords.
  const [snapping, setSnapping] = useState(false);
  const autoSnapToGrid = async () => {
    if (snapping) return;
    if (!(await ensureSavedBeforeAITool('Auto-snap'))) return;
    setSnapping(true);
    try {
      const res = await snapBoardLayoutFrames(id, { tolerancePct: 30 });
      setLayout((l) => {
        const next = { ...l, frames: res.layout.frames };
        setSavedSnapshot(buildLayoutSnapshot(next));
        return next;
      });
      const moved = res.snappedCount || 0;
      const total = res.totalFrames || 0;
      if (moved === 0) {
        toast.success('Already aligned — no frames needed adjusting');
      } else {
        toast.success(`Snapped ${moved} of ${total} frame${total === 1 ? '' : 's'} to the nearest edges`);
      }
    } catch (e) {
      console.error('Auto-snap failed', e);
      toast.error(aiToolErrorMessage(e, 'Auto-snap failed'));
    } finally {
      setSnapping(false);
    }
  };

  // Equalise sizes — polish-pass that resizes every frame within a
  // type to its peer-group median (w, h), pinning the frame's centre.
  // Boards where every breaker is the same physical size end up
  // pixel-identical breaker rectangles in one click.
  const [equalising, setEqualising] = useState(false);
  const equaliseSizes = async () => {
    if (equalising) return;
    if (!(await ensureSavedBeforeAITool('Equalise'))) return;
    setEqualising(true);
    try {
      const res = await equaliseBoardLayoutFrames(id, {});
      setLayout((l) => {
        const next = { ...l, frames: res.layout.frames };
        setSavedSnapshot(buildLayoutSnapshot(next));
        return next;
      });
      const moved = res.equalisedCount || 0;
      const total = res.totalFrames || 0;
      if (moved === 0) {
        toast.success('Already uniform — every type already shares one size');
      } else {
        toast.success(`Equalised ${moved} of ${total} frame${total === 1 ? '' : 's'} to type median`);
      }
    } catch (e) {
      console.error('Equalise failed', e);
      toast.error(aiToolErrorMessage(e, 'Equalise failed'));
    } finally {
      setEqualising(false);
    }
  };

  // Generate from photo — auto-detect the breaker grid bounds in the
  // reference photo and create `phases × ways` cells in one shot.
  // Best paired with Auto-snap → Equalise as the next two clicks.
  // We pass the LIVE form state for phases/ways so Generate works even
  // when the user has typed a new value but hasn't clicked Save yet.
  const [generating, setGenerating] = useState(false);
  const generateFromPhoto = async () => {
    if (generating) return;
    const liveWays = Number(layout?.ways) || 0;
    const livePhases = Number(layout?.phases) || 0;
    if (liveWays < 1) {
      toast.error('Set Ways > 0 first so I know the grid size');
      return;
    }
    const hasBreakers = (layout.frames || []).some((f) => f.type === 'breaker');
    if (hasBreakers && !window.confirm(
      `Replace ${layout.frames.filter((f) => f.type === 'breaker').length} existing breaker frames with auto-detected ones? Isolator and circuit-label frames will be preserved.`,
    )) return;
    if (!(await ensureSavedBeforeAITool('Generate'))) return;
    setGenerating(true);
    try {
      const res = await generateBoardLayoutFrames(id, {
        phases: livePhases || undefined,
        ways: liveWays,
      });
      setLayout((l) => {
        const next = { ...l, frames: res.layout.frames, phases: res.phases ?? l.phases, ways: res.ways ?? l.ways };
        setSavedSnapshot(buildLayoutSnapshot(next));
        return next;
      });
      const n = res.generatedCount || 0;
      toast.success(`Generated ${n} cell${n === 1 ? '' : 's'} (${res.phases}-phase × ${res.ways}-way). Run Auto-snap to polish.`);
    } catch (e) {
      console.error('Generate from photo failed', e);
      toast.error(aiToolErrorMessage(e, 'Generate failed'));
    } finally {
      setGenerating(false);
    }
  };

  // Detect phases — sample wire colours inside breaker frames and
  // re-number L1/L2/L3 to match the picked scheme. The engineer picks
  // which scheme (modern brown/black/grey OR legacy red/yellow/blue)
  // because there's no reliable way to auto-tell from pixels alone.
  const [detectingPhases, setDetectingPhases] = useState(false);
  const detectPhases = async (colourScheme) => {
    if (detectingPhases) return;
    if (!(await ensureSavedBeforeAITool('Detect phases'))) return;
    setDetectingPhases(true);
    try {
      const res = await detectBoardLayoutPhases(id, { colourScheme });
      setLayout((l) => {
        const next = { ...l, frames: res.layout.frames };
        setSavedSnapshot(buildLayoutSnapshot(next));
        return next;
      });
      const moved = res.relabelledCount || 0;
      if (moved === 0) {
        toast.success(`Phases already correctly ordered for ${colourScheme} scheme`);
      } else {
        const summary = (res.mapping || [])
          .map((m) => `${m.phase} (${m.colour}) ← row ${m.oldRow}`)
          .join(' · ');
        toast.success(`Re-labelled ${moved} frame${moved === 1 ? '' : 's'} · ${summary}`);
      }
    } catch (e) {
      console.error('Phase detection failed', e);
      toast.error(aiToolErrorMessage(e, 'Phase detection failed'));
    } finally {
      setDetectingPhases(false);
    }
  };

  const selected = useMemo(
    () => layout?.frames?.find((f) => f.id === selectedId) || null,
    [layout, selectedId],
  );

  // Fetch the server-rendered crop for the selected frame. Caller is
  // responsible for the click — we don't auto-fetch because each call
  // round-trips multi-MB photo bytes through the cropper. We DO bust
  // the cached blob URL on unmount + when the user picks a different
  // frame to avoid leaks and stale previews.
  const loadServerCrop = async () => {
    if (!selected || !layout?.hasPhoto) return;
    setServerCropLoading(true);
    setServerCropError(null);
    try {
      // We must save first if the frame coords are unsaved — the
      // server crops from the saved DB doc, not the live form state.
      // `save()` is idempotent so re-saving when nothing has changed
      // is cheap, and it keeps the "Unsaved changes" pill in sync.
      if (isDirty) {
        const ok = await save();
        if (!ok) {
          setServerCropError('Save failed — frame coords could not be flushed to server');
          return;
        }
      }
      const url = await fetchBoardLayoutFrameCropBlobUrl(id, selected.id);
      // Revoke the previous blob URL to avoid leaking the older crop
      // bytes — these can be 50–200 KB each and stack up fast as the
      // engineer hops between frames.
      if (serverCropUrl) URL.revokeObjectURL(serverCropUrl);
      setServerCropUrl(url);
      setServerCropFrameId(selected.id);
    } catch (e) {
      console.error('Server crop preview failed', e);
      setServerCropError(e?.message || 'Server crop failed');
    } finally {
      setServerCropLoading(false);
    }
  };

  // Reset the server crop when the engineer picks a different frame
  // (so they don't see a stale image labelled with the wrong frame).
  useEffect(() => {
    if (serverCropFrameId && selected?.id !== serverCropFrameId) {
      if (serverCropUrl) URL.revokeObjectURL(serverCropUrl);
      setServerCropUrl(null);
      setServerCropFrameId(null);
      setServerCropError(null);
    }
  }, [selected?.id, serverCropFrameId, serverCropUrl]);

  // Revoke any outstanding server-crop blob URL on unmount.
  useEffect(() => () => {
    if (serverCropUrl) URL.revokeObjectURL(serverCropUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live slot list derived from current phases/ways. The backend also
  // computes slots on read, but we can't wait for a save round-trip
  // when the engineer is dialling in the Phases / Ways inputs.
  const liveSlots = useMemo(
    () => buildSlotsClient(layout?.phases, layout?.ways),
    [layout?.phases, layout?.ways],
  );

  if (loading || !layout) {
    return <div className="p-8 text-sm text-slate-500">Loading editor…</div>;
  }

  const photoUrl = layout.hasPhoto ? photoBlobUrl : null;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4" data-testid="board-layout-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/board-layouts')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <Input
              className="font-semibold text-base"
              value={layout.name || ''}
              onChange={(e) => setLayout((l) => ({ ...l, name: e.target.value }))}
              data-testid="board-layout-name-input"
            />
            <Input
              className="text-xs text-slate-500 mt-1"
              value={layout.description || ''}
              placeholder="Description"
              onChange={(e) => setLayout((l) => ({ ...l, description: e.target.value }))}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDirty ? (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-xs font-medium px-2.5 py-1 animate-pulse"
              data-testid="unsaved-changes-pill"
              title="Form has unsaved edits — click Save layout (or any AI tool will auto-save first)"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Unsaved changes
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium px-2.5 py-1"
              data-testid="saved-pill"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Saved
            </span>
          )}
          <Button
            onClick={save}
            disabled={saving || !isDirty}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="save-board-layout-btn"
          >
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save layout'}
          </Button>
        </div>
      </div>

      {/* ── Board configuration — phases + ways drive the slot list ── */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-end gap-4 flex-wrap">
        <div>
          <Label className="text-xs">Phases</Label>
          <Select
            value={String(layout.phases || 3)}
            onValueChange={(v) => setLayout((l) => ({ ...l, phases: Number(v) }))}
          >
            <SelectTrigger className="mt-1 w-32" data-testid="layout-phases-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1-phase</SelectItem>
              <SelectItem value="3">3-phase</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Ways</Label>
          <Input
            type="number"
            min={0}
            max={60}
            className="mt-1 w-32"
            value={layout.ways || 0}
            onChange={(e) => setLayout((l) => ({ ...l, ways: Math.max(0, Math.min(60, Number(e.target.value) || 0)) }))}
            data-testid="layout-ways-input"
          />
        </div>
        <div className="flex-1 min-w-[200px] text-xs text-slate-600">
          <div className="font-medium text-slate-900">
            {liveSlots.length} circuit slot{liveSlots.length === 1 ? '' : 's'}
            {liveSlots.length > 0 && (
              <>: <span className="font-mono">{liveSlots[0].label}</span> → <span className="font-mono">{liveSlots[liveSlots.length - 1].label}</span></>
            )}
          </div>
          <p className="text-slate-500 mt-1 leading-snug">
            Drives the certificate's pre-populated circuit list. Use <span className="font-medium">Auto-number frames</span> to assign every slot to a frame in row-major order.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={autoNumberFrames}
          disabled={!layout.frames?.length || !liveSlots.length}
          data-testid="auto-number-frames-btn"
          title="Sort breaker frames top-to-bottom, left-to-right and assign each one a slot in order"
        >
          <Hash className="h-4 w-4 mr-1" /> Auto-number frames
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* ─── Left palette ─── */}
        <aside className="col-span-12 lg:col-span-2 bg-white border border-slate-200 rounded-md p-3 space-y-2 self-start">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">Palette</div>
          {FRAME_TYPES.map((t) => (
            <button
              key={t.type}
              onClick={() => addFrame(t.type)}
              className={`w-full text-left px-3 py-2.5 rounded border-2 ${t.color} hover:opacity-90 transition-opacity`}
              data-testid={`add-frame-${t.type}-btn`}
            >
              <div className="flex items-center gap-2">
                <span className={`inline-block h-3 w-3 rounded-sm ${t.chip}`} />
                <span className="font-medium text-sm">{t.label}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t.desc}</div>
            </button>
          ))}

          <div className="pt-3 mt-3 border-t border-slate-200 space-y-2">
            <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Reference photo</div>
            {!layout.hasPhoto ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                data-testid="upload-reference-photo-btn"
              >
                <Upload className="h-4 w-4 mr-1" />
                {uploadingPhoto ? 'Uploading…' : 'Upload photo'}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                onClick={handlePhotoRemove}
              >
                <X className="h-4 w-4 mr-1" /> Remove photo
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
              className="hidden"
              onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
              data-testid="reference-photo-file-input"
            />
            <p className="text-[11px] text-slate-500 leading-snug">
              Optional. Drop in a real photo of the board so you can place frames accurately.
            </p>
          </div>
        </aside>

        {/* ─── Canvas ─── */}
        <main className="col-span-12 lg:col-span-7">
          {/* Canvas toolbar — copy / paste / select-all / clear. The
              same actions are bound to keyboard shortcuts (Cmd/Ctrl
              +C / +V / +A, Esc, Delete) but the buttons keep the UX
              discoverable for engineers who don't know them. */}
          <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-1 flex-wrap">
              <Button
                variant="outline" size="sm"
                disabled={!selectedCount}
                onClick={copySelected}
                data-testid="copy-frames-btn"
                title="Copy selected frames (Ctrl/Cmd+C)"
              >
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                {selectedCount > 0 && <span className="ml-1 text-[10px] tabular-nums opacity-70">({selectedCount})</span>}
              </Button>
              <Button
                variant="outline" size="sm"
                disabled={!frameClipboard.length}
                onClick={pasteFromClipboard}
                data-testid="paste-frames-btn"
                title="Paste frames at +3% / +3% offset (Ctrl/Cmd+V)"
              >
                <ClipboardPaste className="h-3.5 w-3.5 mr-1" /> Paste
                {frameClipboard.length > 0 && <span className="ml-1 text-[10px] tabular-nums opacity-70">({frameClipboard.length})</span>}
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => {
                  if (!layout?.frames?.length) return;
                  setSelectedIds(new Set(layout.frames.map((f) => f.id)));
                  setLastSelectedId(layout.frames[layout.frames.length - 1].id);
                }}
                data-testid="select-all-frames-btn"
                title="Select all frames (Ctrl/Cmd+A)"
              >
                Select all
              </Button>
              {selectedCount > 0 && (
                <Button
                  variant="outline" size="sm"
                  onClick={() => selectFrame(null)}
                  data-testid="clear-selection-btn"
                >
                  Clear ({selectedCount})
                </Button>
              )}
              {photoUrl && layout.frames.length > 0 && (
                <Button
                  variant="outline" size="sm"
                  onClick={autoSnapToGrid}
                  disabled={snapping}
                  data-testid="auto-snap-btn"
                  title="Detect rectangle outlines in the reference photo and nudge each frame to the nearest match"
                  className="ml-auto bg-slate-900 text-white hover:bg-slate-800 hover:text-white border-slate-900"
                >
                  <Wand2 className="h-3.5 w-3.5 mr-1" /> {snapping ? 'Snapping…' : 'Auto-snap to grid'}
                </Button>
              )}
              {photoUrl && (layout.ways || 0) > 0 && (
                <Button
                  variant="outline" size="sm"
                  onClick={generateFromPhoto}
                  disabled={generating}
                  data-testid="generate-frames-btn"
                  title="Auto-detect the breaker grid in the reference photo and create one frame per cell"
                  className="bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white border-emerald-600"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> {generating ? 'Generating…' : 'Generate from photo'}
                </Button>
              )}
              {photoUrl && (layout.frames || []).some((f) => f.type === 'breaker') && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline" size="sm"
                      disabled={detectingPhases}
                      data-testid="detect-phases-btn"
                      title="Sample wire colours inside breaker frames and re-number L1/L2/L3 accordingly"
                      className="bg-indigo-600 text-white hover:bg-indigo-700 hover:text-white border-indigo-600"
                    >
                      <Palette className="h-3.5 w-3.5 mr-1" />
                      {detectingPhases ? 'Detecting…' : 'Detect phases'}
                      <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-80" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <DropdownMenuLabel className="text-xs">Pick the wire-colour convention</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => detectPhases('modern')}
                      data-testid="detect-phases-modern-item"
                      className="flex flex-col items-start gap-1"
                    >
                      <span className="font-medium text-sm">Modern (post-2004)</span>
                      <span className="flex items-center gap-2 text-xs text-slate-600">
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: '#5f3a1e' }} /> Brown · L1
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-slate-900" /> Black · L2
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-slate-400" /> Grey · L3
                        </span>
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => detectPhases('legacy')}
                      data-testid="detect-phases-legacy-item"
                      className="flex flex-col items-start gap-1"
                    >
                      <span className="font-medium text-sm">Legacy (pre-2004)</span>
                      <span className="flex items-center gap-2 text-xs text-slate-600">
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-600" /> Red · L1
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-yellow-400" /> Yellow · L2
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-blue-600" /> Blue · L3
                        </span>
                      </span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {layout.frames.length > 1 && (
                <Button
                  variant="outline" size="sm"
                  onClick={equaliseSizes}
                  disabled={equalising}
                  data-testid="equalise-sizes-btn"
                  title="Resize every frame in each type to the type's median (w, h), pinning each frame's centre"
                  className={photoUrl ? '' : 'ml-auto'}
                >
                  <Ruler className="h-3.5 w-3.5 mr-1" /> {equalising ? 'Equalising…' : 'Equalise sizes'}
                </Button>
              )}
            </div>
            <span className="text-[11px] text-slate-500">
              Shift-click to add to selection · Ctrl/Cmd-click to toggle · drag any selected frame to move the group
            </span>
          </div>
          <Canvas
            ref={canvasRef}
            layout={layout}
            photoUrl={photoUrl}
            isFrameSelected={isFrameSelected}
            selectFrame={selectFrame}
            updateFrame={updateFrame}
            moveSelectedFrames={moveSelectedFrames}
            selectedIds={selectedIds}
          />
          <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1">
            <Move className="h-3 w-3" /> Drag a frame to move it · drag the bottom-right corner to resize · click to select · Shift-click to add to selection.
          </div>
        </main>

        {/* ─── Right properties panel ─── */}
        <aside className="col-span-12 lg:col-span-3 bg-white border border-slate-200 rounded-md p-4 self-start">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-3">
            {selectedCount > 1
              ? `${selectedCount} frames selected`
              : selected ? 'Selected frame' : 'No selection'}
          </div>
          {selectedCount > 1 ? (
            <div className="space-y-3">
              <p className="text-xs text-slate-600 leading-snug">
                Drag any selected frame to move them as a group. Use Copy / Paste to clone the selection, or Delete to remove all of them.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={copySelected}
                data-testid="props-copy-selected-btn"
              >
                <Copy className="h-4 w-4 mr-1" /> Copy {selectedCount} frame{selectedCount === 1 ? '' : 's'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                onClick={deleteSelected}
                data-testid="props-delete-selected-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" /> Delete {selectedCount} frame{selectedCount === 1 ? '' : 's'}
              </Button>
            </div>
          ) : selected ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Type</Label>
                <div className="mt-1 flex items-center gap-2 text-sm">
                  <span className={`inline-block h-3 w-3 rounded-sm ${frameMeta(selected.type).chip}`} />
                  <span className="font-medium">{frameMeta(selected.type).label}</span>
                </div>
              </div>
              <div>
                <Label className="text-xs">Slot binding</Label>
                <Select
                  value={selected.cctId ? String(selected.cctId) : 'none'}
                  onValueChange={(v) => {
                    if (v === 'none') return assignSlotToFrame(selected.id, null);
                    const slot = liveSlots.find((s) => String(s.cctId) === v);
                    assignSlotToFrame(selected.id, slot);
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="frame-slot-select">
                    <SelectValue placeholder="No slot — free-form label" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {liveSlots.map((s) => (
                      <SelectItem key={s.cctId} value={String(s.cctId)}>
                        CCT {s.cctId} · {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                  Pick a slot from the auto-generated list above. The frame's label and CCT ID will follow.
                </p>
              </div>
              <div>
                <Label className="text-xs">Label</Label>
                <Input
                  className="mt-1"
                  value={selected.label || ''}
                  onChange={(e) => updateFrame(selected.id, { label: e.target.value })}
                  placeholder={selected.type === 'breaker' ? 'e.g. 1 L1' : selected.type === 'isolator' ? 'MAIN' : 'Sockets'}
                  data-testid="selected-frame-label-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <NumField label="X (%)" value={selected.x}
                  onChange={(v) => updateFrame(selected.id, { x: clamp(v, 0, 99) })} />
                <NumField label="Y (%)" value={selected.y}
                  onChange={(v) => updateFrame(selected.id, { y: clamp(v, 0, 99) })} />
                <NumField label="W (%)" value={selected.w}
                  onChange={(v) => updateFrame(selected.id, { w: clamp(v, 1, 100 - selected.x) })} />
                <NumField label="H (%)" value={selected.h}
                  onChange={(v) => updateFrame(selected.id, { h: clamp(v, 1, 100 - selected.y) })} />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => removeFrame(selected.id)}
                data-testid="delete-selected-frame-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" /> Delete frame
              </Button>
              {photoUrl && (
                <div className="pt-3 mt-1 border-t border-slate-100">
                  <Label className="text-xs flex items-center justify-between">
                    <span>AI crop preview</span>
                    <span className="text-[10px] font-normal text-slate-400">live</span>
                  </Label>
                  <FrameCropPreview
                    photoUrl={photoUrl}
                    frame={selected}
                    maxWidth={260}
                    maxHeight={180}
                    className="mt-1 mx-auto"
                    data-testid="selected-frame-crop-preview"
                  />
                  <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                    This is the exact pixel region the AI will see for this frame.
                  </p>

                  {/* Server-side crop verification — fetches the EXACT
                      bytes the training queue + cert vision pipeline
                      will receive. The local canvas above mirrors the
                      math but this is ground truth. Useful as a sanity
                      check before kicking off a long training queue. */}
                  <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <Label className="text-xs">Server crop (ground truth)</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-[11px]"
                        onClick={loadServerCrop}
                        disabled={serverCropLoading}
                        data-testid="server-crop-preview-btn"
                      >
                        {serverCropLoading
                          ? 'Loading…'
                          : (serverCropUrl ? 'Refresh' : 'Verify')}
                      </Button>
                    </div>
                    {serverCropError ? (
                      <p className="text-[11px] text-red-600 leading-snug" data-testid="server-crop-preview-error">
                        {serverCropError}
                      </p>
                    ) : serverCropUrl ? (
                      <img
                        src={serverCropUrl}
                        alt={`Server crop of frame ${selected.label || selected.id}`}
                        className="block mx-auto max-h-[180px] max-w-full rounded border border-slate-200 bg-slate-50"
                        data-testid="server-crop-preview-image"
                      />
                    ) : (
                      <p className="text-[10px] text-slate-500 leading-snug">
                        Click <span className="font-medium">Verify</span> to fetch the actual crop the AI will receive. Saves your changes first so the server crops the latest coords.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              Click a frame on the canvas to edit its label or coordinates. Use the palette on the left to add new frames.
            </p>
          )}

          <div className="pt-4 mt-4 border-t border-slate-200">
            <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Stats</div>
            <ul className="text-xs text-slate-600 space-y-1">
              {FRAME_TYPES.map((t) => {
                const n = layout.frames.filter((f) => f.type === t.type).length;
                return (
                  <li key={t.type} className="flex justify-between">
                    <span className="inline-flex items-center gap-1">
                      <span className={`inline-block h-2.5 w-2.5 rounded-sm ${t.chip}`} />
                      {t.label}
                    </span>
                    <span className="font-mono text-slate-900">{n}</span>
                  </li>
                );
              })}
              <li className="flex justify-between pt-1 border-t border-slate-100 font-medium">
                <span>Total</span>
                <span className="font-mono">{layout.frames.length}</span>
              </li>
            </ul>
          </div>
        </aside>
      </div>

      {/* ─── Verify AI alignment ─── full-width grid of every frame's crop */}
      {photoUrl && layout.frames.length > 0 && (
        <VerifyAlignmentGrid
          photoUrl={photoUrl}
          frames={layout.frames}
          selectedIds={selectedIds}
          onFrameClick={(id) => setSelectedIds(new Set([id]))}
        />
      )}
    </div>
  );
};

const NumField = ({ label, value, onChange }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input
      type="number"
      step="0.5"
      className="mt-1 h-8 text-xs"
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    />
  </div>
);

// ─────────────────── Verify alignment grid ───────────────────
// Shows a thumbnail of every frame's exact AI crop region in one
// glance. Clicking a thumbnail selects that frame on the main canvas
// so the engineer can fine-tune coords if a crop drifted.
const VerifyAlignmentGrid = ({ photoUrl, frames, selectedIds, onFrameClick }) => {
  const [open, setOpen] = useState(true);
  const colorByType = {
    breaker: 'border-blue-500',
    isolator: 'border-amber-500',
    circuit_label: 'border-emerald-500',
  };
  return (
    <section
      className="bg-white border border-slate-200 rounded-md p-4"
      data-testid="verify-alignment-grid"
    >
      <header className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">Verify AI alignment</div>
          <div className="text-xs text-slate-500">
            One thumbnail per frame — surfaces coord drift before any AI call.
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen((v) => !v)}
          data-testid="toggle-verify-alignment-btn"
        >
          {open ? 'Hide' : 'Show'} ({frames.length})
        </Button>
      </header>
      {open && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
          {frames.map((f) => {
            const isSel = selectedIds.has(f.id);
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => onFrameClick?.(f.id)}
                className={`group text-left rounded-md border-2 overflow-hidden bg-slate-50 transition-shadow hover:shadow-md ${
                  isSel ? 'ring-2 ring-slate-900 border-slate-900' : colorByType[f.type] || 'border-slate-300'
                }`}
                data-testid={`verify-frame-${f.id}`}
              >
                <FrameCropPreview
                  photoUrl={photoUrl}
                  frame={f}
                  maxWidth={200}
                  maxHeight={120}
                  className="w-full"
                />
                <div className="px-1.5 py-1 bg-white border-t border-slate-200">
                  <div className="text-[11px] font-medium text-slate-900 truncate">
                    {f.label || <span className="text-slate-400 italic">unlabeled</span>}
                  </div>
                  <div className="text-[10px] text-slate-500 capitalize">
                    {String(f.type || '').replace('_', ' ')}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};

// ─────────────────── Canvas component ───────────────────
const Canvas = React.forwardRef(({
  layout, photoUrl,
  isFrameSelected, selectFrame, updateFrame, moveSelectedFrames, selectedIds,
}, ref) => {
  const localRef = useRef(null);
  const containerRef = ref || localRef;
  return (
    <div
      ref={containerRef}
      className="relative w-full bg-slate-100 border border-slate-300 rounded-md overflow-hidden select-none"
      style={{ aspectRatio: '4 / 3', minHeight: 320 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) selectFrame(null);
      }}
      data-testid="board-layout-canvas"
    >
      {/* Background — reference photo or grid */}
      {photoUrl ? (
        <img
          src={photoUrl}
          alt="Reference"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          draggable={false}
        />
      ) : (
        <GridBackground />
      )}

      {/* Frames */}
      {layout.frames.map((f) => (
        <FrameBox
          key={f.id}
          frame={f}
          containerRef={containerRef}
          selected={isFrameSelected(f.id)}
          isPartOfMultiSelection={selectedIds.size > 1 && isFrameSelected(f.id)}
          onSelect={(e) => selectFrame(f.id, { toggle: e.shiftKey || e.metaKey || e.ctrlKey })}
          onChange={(patch) => updateFrame(f.id, patch)}
          moveSelectedFrames={moveSelectedFrames}
        />
      ))}
    </div>
  );
});

const GridBackground = () => (
  <div
    className="absolute inset-0 pointer-events-none"
    style={{
      backgroundImage:
        'linear-gradient(to right, rgba(15,23,42,0.07) 1px, transparent 1px),' +
        'linear-gradient(to bottom, rgba(15,23,42,0.07) 1px, transparent 1px)',
      backgroundSize: '5% 5%',
    }}
  >
    <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs uppercase tracking-widest">
      <ImageIcon className="h-5 w-5 mr-2" /> Drop a reference photo or place frames on the grid
    </div>
  </div>
);

const FrameBox = ({ frame, containerRef, selected, isPartOfMultiSelection, onSelect, onChange, moveSelectedFrames }) => {
  const startRef = useRef(null);
  const meta = frameMeta(frame.type);

  const startDrag = (e, mode /* 'move' | 'resize' */) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(e);
    const rect = containerRef.current.getBoundingClientRect();
    // Capture whether this frame is part of a multi-selection at
    // drag-start. If yes, drag becomes a group-move (every selected
    // frame moves by the same delta). The single-frame fast-path
    // still re-clamps w/h on edge collisions.
    const groupMove = mode === 'move' && isPartOfMultiSelection;
    startRef.current = {
      mode,
      groupMove,
      startX: e.clientX,
      startY: e.clientY,
      origX: frame.x,
      origY: frame.y,
      origW: frame.w,
      origH: frame.h,
      lastDx: 0,
      lastDy: 0,
      boxW: rect.width,
      boxH: rect.height,
    };
    const onMove = (ev) => {
      const s = startRef.current;
      if (!s) return;
      const dxPct = ((ev.clientX - s.startX) / s.boxW) * 100;
      const dyPct = ((ev.clientY - s.startY) / s.boxH) * 100;
      if (s.mode === 'move') {
        if (s.groupMove && moveSelectedFrames) {
          // Apply the *delta* delta — i.e. the change since the last
          // mouse-move event — so every frame in the group tracks the
          // cursor without compounding past offsets.
          const stepDx = snap(dxPct) - s.lastDx;
          const stepDy = snap(dyPct) - s.lastDy;
          if (stepDx !== 0 || stepDy !== 0) {
            moveSelectedFrames(stepDx, stepDy);
            s.lastDx = snap(dxPct);
            s.lastDy = snap(dyPct);
          }
        } else {
          const nx = clamp(snap(s.origX + dxPct), 0, 99);
          const ny = clamp(snap(s.origY + dyPct), 0, 99);
          // Re-clamp w/h so they always fit
          const nw = clamp(frame.w, 1, 100 - nx);
          const nh = clamp(frame.h, 1, 100 - ny);
          onChange({ x: nx, y: ny, w: nw, h: nh });
        }
      } else {
        const nw = clamp(snap(s.origW + dxPct), 1, 100 - frame.x);
        const nh = clamp(snap(s.origH + dyPct), 1, 100 - frame.y);
        onChange({ w: nw, h: nh });
      }
    };
    const onUp = () => {
      startRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div
      className={`absolute border-2 ${meta.color} ${selected ? 'ring-2 ring-offset-1 ring-amber-400 z-20' : 'z-10'} cursor-move`}
      style={{
        left: `${frame.x}%`,
        top: `${frame.y}%`,
        width: `${frame.w}%`,
        height: `${frame.h}%`,
      }}
      onMouseDown={(e) => startDrag(e, 'move')}
      onClick={(e) => { e.stopPropagation(); onSelect(e); }}
      data-testid={`frame-${frame.id}`}
    >
      {frame.label && (
        <div className="absolute inset-0 flex items-center justify-center text-[10px] sm:text-xs font-medium text-slate-700 px-1 truncate pointer-events-none">
          {frame.label}
        </div>
      )}
      {/* Resize handle (bottom-right) — disabled visually for group
          selections so engineers don't expect group-resize. */}
      {!isPartOfMultiSelection && (
        <div
          className={`absolute -right-1 -bottom-1 w-3 h-3 ${meta.chip} cursor-nwse-resize border border-white`}
          onMouseDown={(e) => startDrag(e, 'resize')}
        />
      )}
    </div>
  );
};

export default BoardLayoutEditor;
